import React from "react";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Paper,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
	People as PeopleIcon,
	CheckCircle as CheckCircleIcon,
	Cancel as CancelIcon,
	Refresh as RefreshIcon
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAnalyticsDashboard } from "../../hooks/useAnalytics";
import { zoullooColors } from "../../theme";
import { ProfileStats, WeeklyProfileStats, WeeklyStats } from "../../models/Analytics";

// KPI Card Component
interface KpiCardProps {
	title: string;
	value: number | string;
	icon: React.ReactNode;
	color: string;
	subtitle?: string;
}

const KpiCard: React.FC<KpiCardProps> = ({ title, value, icon, color, subtitle }) => (
	<Card
		sx={{
			height: "100%",
			background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
			border: `1px solid ${color}30`,
			borderRadius: 3,
			transition: "transform 0.2s, box-shadow 0.2s",
			"&:hover": {
				transform: "translateY(-4px)",
				boxShadow: `0 8px 25px ${color}20`
			}
		}}
	>
		<CardContent sx={{ p: 3 }}>
			<Box display="flex" alignItems="center" justifyContent="space-between">
				<Box>
					<Typography variant="body2" color="text.secondary" fontWeight={500}>
						{title}
					</Typography>
					<Typography variant="h3" fontWeight={700} sx={{ color, mt: 1 }}>
						{value}
					</Typography>
					{subtitle && (
						<Typography variant="caption" color="text.secondary">
							{subtitle}
						</Typography>
					)}
				</Box>
				<Box
					sx={{
						backgroundColor: `${color}20`,
						borderRadius: "50%",
						p: 1.5,
						display: "flex",
						alignItems: "center",
						justifyContent: "center"
					}}
				>
					{React.cloneElement(icon as React.ReactElement, {
						sx: { fontSize: 32, color }
					})}
				</Box>
			</Box>
		</CardContent>
	</Card>
);

// Main Stats Table Component
interface StatsTableProps {
	data: ProfileStats[];
	title: string;
}

const StatsTable: React.FC<StatsTableProps> = ({ data, title }) => {
	const { t } = useTranslation();

	const columns = [
		{ key: "profile", label: "Profile", align: "left" as const },
		{ key: "nonSoumis", label: t("analytics.nonSoumis"), align: "right" as const },
		{ key: "soumis", label: t("analytics.soumis"), align: "right" as const },
		{ key: "entretien", label: t("analytics.entretien"), align: "right" as const },
		{ key: "evalSoftSkills", label: t("analytics.evalSoftSkills"), align: "right" as const },
		{ key: "evalHardSkills", label: t("analytics.evalHardSkills"), align: "right" as const },
		{ key: "pipe", label: t("analytics.pipe"), align: "right" as const },
		{ key: "accepte", label: t("analytics.accepte"), align: "right" as const },
		{ key: "refuse", label: t("analytics.refuse"), align: "right" as const },
		{ key: "inscrit", label: t("analytics.inscrit"), align: "right" as const }
	];

	// Calculate totals
	const totals = data.reduce(
		(acc, row) => ({
			inscrit: acc.inscrit + row.inscrit,
			nonSoumis: acc.nonSoumis + row.nonSoumis,
			soumis: acc.soumis + row.soumis,
			entretien: acc.entretien + row.entretien,
			evalSoftSkills: acc.evalSoftSkills + row.evalSoftSkills,
			evalHardSkills: acc.evalHardSkills + row.evalHardSkills,
			pipe: acc.pipe + row.pipe,
			accepte: acc.accepte + row.accepte,
			refuse: acc.refuse + row.refuse
		}),
		{
			inscrit: 0,
			nonSoumis: 0,
			soumis: 0,
			entretien: 0,
			evalSoftSkills: 0,
			evalHardSkills: 0,
			pipe: 0,
			accepte: 0,
			refuse: 0
		}
	);

	const getCellColor = (key: string) => {
		switch (key) {
			case "pipe":
				return "#FFF3CD";
			case "accepte":
				return "#C6EFCE";
			case "refuse":
				return "#FFC7CE";
			default:
				return "transparent";
		}
	};

	return (
		<Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
			<Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
				<Typography variant="h6" fontWeight={600}>
					{title}
				</Typography>
			</Box>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
							{columns.map(col => (
								<TableCell
									key={col.key}
									align={col.align}
									sx={{
										fontWeight: 600,
										fontSize: "0.75rem",
										py: 1.5,
										whiteSpace: "nowrap"
									}}
								>
									{col.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map((row, index) => (
							<TableRow
								key={row.profile}
								sx={{
									"&:hover": { backgroundColor: "#f9f9f9" },
									backgroundColor: index % 2 === 0 ? "white" : "#fafafa"
								}}
							>
								<TableCell sx={{ fontWeight: 600 }}>{row.profile}</TableCell>
								<TableCell align="right">{row.nonSoumis}</TableCell>
								<TableCell align="right">{row.soumis}</TableCell>
								<TableCell align="right">{row.entretien}</TableCell>
								<TableCell align="right">{row.evalSoftSkills}</TableCell>
								<TableCell align="right">{row.evalHardSkills}</TableCell>
								<TableCell align="right" sx={{ backgroundColor: getCellColor("pipe") }}>
									{row.pipe}
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: getCellColor("accepte") }}>
									{row.accepte}
								</TableCell>
								<TableCell align="right" sx={{ backgroundColor: getCellColor("refuse") }}>
									{row.refuse}
								</TableCell>
								<TableCell align="right">{row.inscrit}</TableCell>
							</TableRow>
						))}
						{/* Total Row */}
						<TableRow sx={{ backgroundColor: "#e3f2fd" }}>
							<TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.nonSoumis}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.soumis}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.entretien}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.evalSoftSkills}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.evalHardSkills}
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 700, backgroundColor: getCellColor("pipe") }}
							>
								{totals.pipe}
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 700, backgroundColor: getCellColor("accepte") }}
							>
								{totals.accepte}
							</TableCell>
							<TableCell
								align="right"
								sx={{ fontWeight: 700, backgroundColor: getCellColor("refuse") }}
							>
								{totals.refuse}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totals.inscrit}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

// Weekly Stats Mini Table
interface WeeklyStatsTableProps {
	title: string;
	data: WeeklyProfileStats[];
	columnLabel: string;
}

const WeeklyStatsTable: React.FC<WeeklyStatsTableProps> = ({ title, data, columnLabel }) => {
	const { t } = useTranslation();

	const totalS1 = data.reduce((sum, d) => sum + d.countS1, 0);
	const totalS2 = data.reduce((sum, d) => sum + d.countS2, 0);

	return (
		<Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
			<Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
				<Typography variant="subtitle1" fontWeight={600}>
					{title}
				</Typography>
			</Box>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
							<TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>Profile</TableCell>
							<TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
								{t("analytics.lastWeek")} {columnLabel}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
								{t("analytics.last2Weeks")} {columnLabel}
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{data.map(row => (
							<TableRow key={row.profile} sx={{ "&:hover": { backgroundColor: "#f9f9f9" } }}>
								<TableCell sx={{ fontWeight: 500 }}>{row.profile}</TableCell>
								<TableCell align="right">{row.countS1}</TableCell>
								<TableCell align="right">{row.countS2}</TableCell>
							</TableRow>
						))}
						<TableRow sx={{ backgroundColor: "#e3f2fd" }}>
							<TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totalS1}
							</TableCell>
							<TableCell align="right" sx={{ fontWeight: 700 }}>
								{totalS2}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

// Simple Stats Card Component (for client registrations)
interface SimpleStatsCardProps {
	title: string;
	data: WeeklyStats;
}

const SimpleStatsCard: React.FC<SimpleStatsCardProps> = ({ title, data }) => {
	const { t } = useTranslation();

	return (
		<Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e0e0e0", overflow: "hidden" }}>
			<Box sx={{ p: 2, borderBottom: "1px solid #e0e0e0", backgroundColor: "#fafafa" }}>
				<Typography variant="subtitle1" fontWeight={600}>
					{title}
				</Typography>
			</Box>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow sx={{ backgroundColor: "#f5f5f5" }}>
							<TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
								{t("analytics.lastWeek")}
							</TableCell>
							<TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
								{t("analytics.last2Weeks")}
							</TableCell>
							<TableCell sx={{ fontWeight: 600, fontSize: "0.75rem" }}>
								Total
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						<TableRow sx={{ backgroundColor: "#e8f5e9" }}>
							<TableCell sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
								{data.countS1}
							</TableCell>
							<TableCell sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
								{data.countS2}
							</TableCell>
							<TableCell sx={{ fontWeight: 700, fontSize: "1.1rem" }}>
								{data.total}
							</TableCell>
						</TableRow>
					</TableBody>
				</Table>
			</TableContainer>
		</Paper>
	);
};

// Loading Skeleton
const DashboardSkeleton: React.FC = () => (
	<Box px={4} py={3}>
		<Skeleton variant="text" width={300} height={40} sx={{ mb: 3 }} />
		<Grid container spacing={3} sx={{ mb: 4 }}>
			{[1, 2, 3, 4].map(i => (
				<Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
					<Skeleton variant="rounded" height={140} sx={{ borderRadius: 3 }} />
				</Grid>
			))}
		</Grid>
		<Skeleton variant="rounded" height={300} sx={{ borderRadius: 3, mb: 3 }} />
		<Grid container spacing={3}>
			<Grid size={{ xs: 12, md: 6 }}>
				<Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
			</Grid>
			<Grid size={{ xs: 12, md: 6 }}>
				<Skeleton variant="rounded" height={200} sx={{ borderRadius: 3 }} />
			</Grid>
		</Grid>
	</Box>
);

// Main Dashboard Component
const AnalyticsDashboard: React.FC = () => {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch, isFetching } = useAnalyticsDashboard();

	if (isLoading) {
		return <DashboardSkeleton />;
	}

	if (isError || !data) {
		return (
			<Box px={4} py={3}>
				<Paper sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
					<Typography variant="h6" color="error" gutterBottom>
						{t("analytics.errorLoading")}
					</Typography>
					<Typography variant="body2" color="text.secondary">
						{t("analytics.tryAgain")}
					</Typography>
				</Paper>
			</Box>
		);
	}

	return (
		<Box px={4} py={3}>
			{/* Header */}
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
				<Box>
					<Typography variant="h4" fontWeight={700} color="text.primary">
						{t("analytics.title")}
					</Typography>
					<Typography variant="body2" color="text.secondary" mt={0.5}>
						{t("analytics.subtitle")}
					</Typography>
				</Box>
				<Box display="flex" alignItems="center" gap={2}>
					<Typography variant="caption" color="text.secondary">
						{t("analytics.lastUpdated")}: {new Date(data.lastRefreshed).toLocaleString()}
					</Typography>
					<Box
						onClick={() => refetch()}
						sx={{
							cursor: "pointer",
							p: 1,
							borderRadius: "50%",
							"&:hover": { backgroundColor: "#f0f0f0" }
						}}
					>
						{isFetching ? (
							<CircularProgress size={20} />
						) : (
							<RefreshIcon sx={{ color: "text.secondary" }} />
						)}
					</Box>
				</Box>
			</Box>

			{/* KPI Cards */}
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<KpiCard
						title={t("analytics.totalInscriptions")}
						value={data.globalKpis.totalInscriptions.toLocaleString()}
						icon={<PeopleIcon />}
						color={zoullooColors.primary.main}
						subtitle={t("analytics.sinceBeginning")}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<KpiCard
						title={t("analytics.totalAcceptes")}
						value={data.globalKpis.totalAcceptes.toLocaleString()}
						icon={<CheckCircleIcon />}
						color="#4CAF50"
						subtitle={`${data.globalKpis.tauxConversionPourcent}% ${t("analytics.conversionRate")}`}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }}>
					<KpiCard
						title={t("analytics.totalRefuses")}
						value={data.globalKpis.totalRefuses.toLocaleString()}
						icon={<CancelIcon />}
						color="#f44336"
					/>
				</Grid>
			</Grid>

			{/* Main Stats Table */}
			<Box mb={4}>
				<StatsTable data={data.profileStatistics} title={t("analytics.freelancersByStatus")} />
			</Box>

			{/* Weekly Stats */}
			<Grid container spacing={3}>
				<Grid size={{ xs: 12, md: 4 }}>
					<WeeklyStatsTable
						title={t("analytics.acceptedCandidates")}
						data={data.acceptedWeekly}
						columnLabel={t("analytics.accepted")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<WeeklyStatsTable
						title={t("analytics.newSubscriptions")}
						data={data.subscriptionsWeekly}
						columnLabel={t("analytics.subscriptions")}
					/>
				</Grid>
				<Grid size={{ xs: 12, md: 4 }}>
					<SimpleStatsCard
						title={t("analytics.clientRegistrations")}
						data={data.clientRegistrations}
					/>
				</Grid>
			</Grid>
		</Box>
	);
};

export default AnalyticsDashboard;
